import React, { createContext, useContext } from "react"
import { useAppSelector } from "@/store/store"
import {
  selectAddresses,
  selectFirstName,
  selectUserId,
} from "@/selectors/auth.selectors"
import { useCartStore } from "@/store/cart/cart-store"
import { selectCartItems } from "@/store/cart/cart.selectors"
import { useEffectiveFulfilment } from "@/hooks/use-effective-fulfilment"
import {
  useBillingPreviewSync,
  type BillingPreviewSyncResult,
} from "@/hooks/use-billing-preview-sync"

const BillingPreviewSyncContext = createContext<BillingPreviewSyncResult | null>(null)

type BillingPreviewSyncProviderProps = {
  children: React.ReactNode
  /**
   * Optional catalog fingerprint for checkout gate cross-checks.
   * Cart / browse hosts omit this; checkout may pass validity catalog fingerprint.
   */
  catalogFingerprint?: string
}

/**
 * Session-level host for billing preview synchronization.
 *
 * UI components (OfferSelectorSheet, DiscountBlock, coupons) only write Redux `discounts.selected`.
 * This provider observes selection + cart and keeps backend billing preview in sync.
 *
 * Pricing Freshness ADR: sole owner boundary for input epoch, Preview network lifecycle, and
 * Pricing Confirmation (via {@link useBillingPreviewSync} → {@link import("@/hooks/usePricingPreview").usePricingPreview}).
 * Cart Continue publishes successful Preview HTTP into that same owner (does not create a second store).
 * Redux `billingPreview` remains a read-model projection; safe-query memory is transport memo only.
 */
export function BillingPreviewSyncProvider({
  children,
  catalogFingerprint,
}: BillingPreviewSyncProviderProps) {
  const userId = useAppSelector(selectUserId)
  const firstName = useAppSelector(selectFirstName)
  const addresses = useAppSelector(selectAddresses)
  const items = useCartStore(selectCartItems)
  const defaultAddr = addresses.find((a) => a.isDefault)
  const { orderType } = useEffectiveFulfilment()

  const enabled = items.length > 0 && Boolean(userId && firstName)

  const sync = useBillingPreviewSync({
    enabled,
    userName: firstName || "",
    orderType,
    addressFingerprint: String(defaultAddr?.address_id ?? ""),
    catalogFingerprint: catalogFingerprint ?? "",
  })

  return (
    <BillingPreviewSyncContext.Provider value={sync}>{children}</BillingPreviewSyncContext.Provider>
  )
}

/** Read sync status / committed pricing. Must be under {@link BillingPreviewSyncProvider}. */
export function useBillingPreviewSyncContext(): BillingPreviewSyncResult {
  const ctx = useContext(BillingPreviewSyncContext)
  if (!ctx) {
    throw new Error("useBillingPreviewSyncContext must be used within BillingPreviewSyncProvider")
  }
  return ctx
}

/** Optional read when a subtree may render outside the provider (e.g. isolated tests). */
export function useOptionalBillingPreviewSync(): BillingPreviewSyncResult | null {
  return useContext(BillingPreviewSyncContext)
}
